/**
 * Public registration page with role-aware flow.
 *
 * - Guest        → minimal form (username + email + password)
 * - Social Actor → comprehensive form + 4 required docs + 2 optional
 * - Investor     → comprehensive form + 4 required docs
 *
 * Admin / Project Manager cannot be created here — those accounts are
 * created only by an existing admin from /admin/users.
 *
 * After submission, social actors and investors land on a Pending Approval
 * confirmation screen.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, CheckCircle2, Circle, Loader2,
  UserPlus, Building2, Briefcase, UserCircle, Upload, Trash2, FileCheck2,
} from 'lucide-react';

const API = `${BACKEND_URL}/api`;

const ROLE_OPTIONS = [
  {
    value: 'guest',
    title: 'Guest',
    subtitle: 'Browse and contribute ideas',
    description: 'Quick access — minimal info required, no review needed.',
    icon: UserCircle,
  },
  {
    value: 'social_actor',
    title: 'Social Actor',
    subtitle: 'Organisations delivering social impact',
    description: 'Comprehensive registration including legal documents. Reviewed by an administrator.',
    icon: Building2,
  },
  {
    value: 'investor',
    title: 'Social Investor',
    subtitle: 'Investors supporting social organisations',
    description: 'Comprehensive registration including financial documents. Reviewed by an administrator.',
    icon: Briefcase,
  },
];

const COUNTRIES = [
  'GR', 'CY', 'IT', 'ES', 'PT', 'FR', 'DE', 'NL', 'BE', 'AT', 'IE', 'PL', 'RO', 'BG', 'HU', 'CZ', 'SK', 'SI', 'HR', 'EE', 'LV', 'LT', 'FI', 'SE', 'DK', 'LU', 'MT', 'Other',
];

const LEGAL_FORMS = [
  'Non-profit', 'Cooperative', 'Foundation', 'Charity', 'Social Enterprise', 'NGO', 'Private Limited Company', 'Public Limited Company', 'Partnership', 'Other',
];

const RegisterPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);   // 1=role 2=details 3=password 4=docs 5=done
  const [role, setRole] = useState(null);
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [userId, setUserId] = useState(null);
  const [docTypes, setDocTypes] = useState({ required: [], optional: [] });
  const [uploaded, setUploaded] = useState({}); // { document_type: {id, filename, size} }
  const [uploading, setUploading] = useState(null);

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const isPending = role === 'social_actor' || role === 'investor';

  // Load document checklist as soon as a role is picked
  useEffect(() => {
    if (!role || !isPending) return;
    axios.get(`${API}/auth/registration-document-types/${role}`).then((r) => setDocTypes(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  // ─────────────────── Step 1: Role picker ───────────────────
  const stepRole = (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">What kind of account would you like to create?</p>
      <div className="grid grid-cols-1 gap-3">
        {ROLE_OPTIONS.map((o) => {
          const Icon = o.icon;
          const selected = role === o.value;
          return (
            <button
              key={o.value}
              onClick={() => setRole(o.value)}
              className={`text-left rounded-lg border-2 p-4 transition ${selected ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`}
              data-testid={`role-${o.value}`}
            >
              <div className="flex items-start gap-3">
                <Icon className={`h-6 w-6 mt-0.5 flex-shrink-0 ${selected ? 'text-blue-600' : 'text-gray-500'}`} />
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">{o.title}</p>
                  <p className="text-xs text-blue-700">{o.subtitle}</p>
                  <p className="text-xs text-gray-600 mt-1">{o.description}</p>
                </div>
                {selected && <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0" />}
              </div>
            </button>
          );
        })}
      </div>
      <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900">
        <strong>Note:</strong> Administrator and Project Manager accounts can only be created by an
        existing administrator from the Admin area.
      </div>
    </div>
  );

  // ─────────────────── Step 2: Details ───────────────────
  const stepDetails = (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Label className="text-xs">Username *</Label>
          <Input value={form.username || ''} onChange={(e) => change('username', e.target.value)} data-testid="reg-username" autoComplete="username" />
        </div>
        <div className="col-span-2">
          <Label className="text-xs">Email *</Label>
          <Input type="email" value={form.email || ''} onChange={(e) => change('email', e.target.value)} data-testid="reg-email" />
        </div>
        {isPending && (
          <>
            <div className="col-span-2">
              <Label className="text-xs">Organisation name *</Label>
              <Input value={form.organization_name || ''} onChange={(e) => change('organization_name', e.target.value)} data-testid="reg-org" />
            </div>
            <div>
              <Label className="text-xs">Legal form *</Label>
              <select className="w-full border rounded-md px-2 py-1.5 text-sm bg-white" value={form.legal_form || ''} onChange={(e) => change('legal_form', e.target.value)} data-testid="reg-legal-form">
                <option value="">Select…</option>
                {LEGAL_FORMS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Country *</Label>
              <select className="w-full border rounded-md px-2 py-1.5 text-sm bg-white" value={form.country || ''} onChange={(e) => change('country', e.target.value)} data-testid="reg-country">
                <option value="">Select…</option>
                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Contact phone *</Label>
              <Input value={form.contact_phone || ''} onChange={(e) => change('contact_phone', e.target.value)} placeholder="+30 210 …" data-testid="reg-phone" />
            </div>
            <div>
              <Label className="text-xs">Tax ID / VAT *</Label>
              <Input value={form.tax_id || ''} onChange={(e) => change('tax_id', e.target.value)} data-testid="reg-tax" />
            </div>
            <div>
              <Label className="text-xs">Year established *</Label>
              <Input type="number" min="1800" max="2100" value={form.year_established || ''} onChange={(e) => change('year_established', e.target.value ? Number(e.target.value) : '')} data-testid="reg-year" />
            </div>
            <div>
              <Label className="text-xs">Website</Label>
              <Input type="url" value={form.website || ''} onChange={(e) => change('website', e.target.value)} placeholder="https://…" data-testid="reg-website" />
            </div>
            {role === 'social_actor' && (
              <>
                <div className="col-span-2">
                  <Label className="text-xs">Sector *</Label>
                  <Input value={form.sector || ''} onChange={(e) => change('sector', e.target.value)} placeholder="e.g. Education, Health, Environment" data-testid="reg-sector" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Mission statement *</Label>
                  <Textarea rows={3} value={form.mission_statement || ''} onChange={(e) => change('mission_statement', e.target.value)} placeholder="What does your organisation aim to achieve?" data-testid="reg-mission" />
                </div>
              </>
            )}
            {role === 'investor' && (
              <>
                <div className="col-span-2">
                  <Label className="text-xs">Investment focus *</Label>
                  <Input value={form.investment_focus || ''} onChange={(e) => change('investment_focus', e.target.value)} placeholder="e.g. Climate, Education, Inclusion" data-testid="reg-focus" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Total capital available (€) *</Label>
                  <Input type="number" min="0" value={form.capital_available ?? ''} onChange={(e) => change('capital_available', e.target.value ? Number(e.target.value) : '')} data-testid="reg-capital" />
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );

  // ─────────────────── Step 3: Password ───────────────────
  const stepPassword = (
    <div className="space-y-3">
      <Label className="text-xs">Password *</Label>
      <Input type="password" value={form.password || ''} onChange={(e) => change('password', e.target.value)} placeholder="Choose a strong password (≥ 8 characters)" data-testid="reg-password" autoComplete="new-password" />
      <Label className="text-xs">Confirm password *</Label>
      <Input type="password" value={form.password_confirm || ''} onChange={(e) => change('password_confirm', e.target.value)} data-testid="reg-password-confirm" autoComplete="new-password" />
      <p className="text-xs text-gray-500">Use at least 8 characters with letters and numbers.</p>
    </div>
  );

  // ─────────────────── Step 4: Documents (only for actor/investor) ───────────────────
  const handleDocUpload = async (docType, file) => {
    if (!userId || !file) return;
    setUploading(docType);
    try {
      const fd = new FormData();
      fd.append('document_type', docType);
      fd.append('file', file);
      const res = await axios.post(`${API}/auth/register/${userId}/documents`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploaded((u) => ({ ...u, [docType]: res.data }));
      toast.success(`${file.name} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(null);
    }
  };

  const allRequiredUploaded = useMemo(() => {
    return (docTypes.required || []).every((d) => uploaded[d.key]);
  }, [docTypes, uploaded]);

  const stepDocuments = (
    <div className="space-y-3">
      <p className="text-xs text-gray-600">
        Please upload the documents below. All <strong>required</strong> documents must be provided
        before submission. Files up to 25 MB are accepted (PDF / DOCX / JPG / PNG).
      </p>
      {[...docTypes.required.map((d) => ({ ...d, required: true })), ...docTypes.optional.map((d) => ({ ...d, required: false }))].map((d) => {
        const u = uploaded[d.key];
        return (
          <div key={d.key} className="border rounded-lg p-3 bg-white" data-testid={`docrow-${d.key}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2 min-w-0">
                {u ? (
                  <CheckCircle2 className="h-5 w-5 text-green-700 flex-shrink-0 mt-0.5" />
                ) : (
                  <Circle className="h-5 w-5 text-gray-300 flex-shrink-0 mt-0.5" />
                )}
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 flex items-center gap-2">
                    {d.label}
                    {d.required && <Badge className="bg-red-100 text-red-700 text-xs">Required</Badge>}
                  </p>
                  {u && <p className="text-xs text-gray-500 truncate">{u.filename} · {(u.size / 1024).toFixed(1)} KB</p>}
                </div>
              </div>
              <label className="flex-shrink-0">
                <input
                  type="file"
                  hidden
                  onChange={(e) => handleDocUpload(d.key, e.target.files?.[0])}
                  data-testid={`docinput-${d.key}`}
                />
                <span className="inline-flex items-center gap-1 text-xs border rounded px-2 py-1 cursor-pointer hover:bg-gray-50">
                  {uploading === d.key ? (
                    <><Loader2 className="h-3 w-3 animate-spin" />Uploading…</>
                  ) : u ? (
                    <><Upload className="h-3 w-3" />Replace</>
                  ) : (
                    <><Upload className="h-3 w-3" />Upload</>
                  )}
                </span>
              </label>
            </div>
          </div>
        );
      })}
    </div>
  );

  // ─────────────────── Step 5: Done ───────────────────
  const stepDone = (
    <div className="text-center py-4 space-y-3">
      <div className="flex justify-center">
        <FileCheck2 className="h-12 w-12 text-green-700" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900">Registration submitted</h3>
      <p className="text-sm text-gray-600">
        {role === 'guest'
          ? 'Your guest account has been created. You can sign in now.'
          : 'Thank you! Your registration is now in the review queue. An administrator will approve or reject your application shortly. You will receive a notification by email and in-platform once a decision is made.'}
      </p>
      <Button onClick={() => navigate('/login')} className="bg-blue-600 hover:bg-blue-700 mt-4" data-testid="reg-go-login">
        Go to Sign In
      </Button>
    </div>
  );

  // ─────────────────── Navigation ───────────────────
  const validateStep = () => {
    if (step === 1) {
      if (!role) { toast.error('Please pick an account type'); return false; }
      return true;
    }
    if (step === 2) {
      if (!form.username || !form.email) { toast.error('Username and email are required'); return false; }
      if (isPending) {
        const required = ['organization_name', 'legal_form', 'country', 'contact_phone', 'tax_id', 'year_established'];
        if (role === 'social_actor') required.push('sector', 'mission_statement');
        if (role === 'investor') required.push('investment_focus', 'capital_available');
        const missing = required.find((k) => !form[k] && form[k] !== 0);
        if (missing) { toast.error('Please fill in all required fields'); return false; }
      }
      return true;
    }
    if (step === 3) {
      if (!form.password || form.password.length < 6) { toast.error('Password must be at least 6 characters'); return false; }
      if (form.password !== form.password_confirm) { toast.error('Passwords do not match'); return false; }
      return true;
    }
    return true;
  };

  const submitRegistration = async () => {
    setSubmitting(true);
    try {
      const payload = { role, username: form.username, email: form.email, password: form.password };
      if (isPending) {
        Object.assign(payload, {
          organization_name: form.organization_name,
          legal_form: form.legal_form,
          country: form.country,
          contact_phone: form.contact_phone,
          tax_id: form.tax_id,
          year_established: form.year_established,
          website: form.website || undefined,
          sector: form.sector || undefined,
          mission_statement: form.mission_statement || undefined,
          investment_focus: form.investment_focus || undefined,
          capital_available: form.capital_available ?? undefined,
        });
      }
      const res = await axios.post(`${API}/auth/register`, payload);
      if (isPending) {
        setUserId(res.data.user_id);
        setStep(4);
        toast.success('Account created — please upload the required documents');
      } else {
        // Guest: token returned, go to done
        if (res.data.access_token) {
          localStorage.setItem('token', res.data.access_token);
        }
        setStep(5);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  const finalize = async () => {
    if (!allRequiredUploaded) { toast.error('Please upload all required documents first'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/register/${userId}/finalize`);
      setStep(5);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not finalize registration');
    } finally {
      setSubmitting(false);
    }
  };

  const next = () => {
    if (!validateStep()) return;
    if (step === 3) {
      submitRegistration();
    } else {
      setStep(step + 1);
    }
  };

  const stepTitle = {
    1: 'Choose account type',
    2: 'Your details',
    3: 'Set a password',
    4: 'Upload supporting documents',
    5: 'Done',
  }[step];

  const totalVisibleSteps = isPending ? 5 : 3;
  const stepNumber = step >= 4 ? (step === 4 ? 4 : totalVisibleSteps) : step;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl shadow-2xl border-0">
        <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-lg">
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Create your account
            </span>
            {step < 5 && (
              <span className="text-xs font-normal opacity-90">
                Step {stepNumber} of {totalVisibleSteps}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <h2 className="text-base font-semibold text-gray-800">{stepTitle}</h2>

          {step === 1 && stepRole}
          {step === 2 && stepDetails}
          {step === 3 && stepPassword}
          {step === 4 && stepDocuments}
          {step === 5 && stepDone}

          {step < 5 && (
            <div className="flex items-center justify-between pt-3 border-t">
              {step > 1 ? (
                <Button variant="outline" onClick={() => setStep(step - 1)} disabled={submitting || step === 4}>
                  <ArrowLeft className="h-4 w-4 mr-1" /> Back
                </Button>
              ) : (
                <Link to="/login" className="text-sm text-gray-500 hover:text-blue-600 flex items-center gap-1">
                  <ArrowLeft className="h-3 w-3" /> Back to sign in
                </Link>
              )}

              {step === 4 ? (
                <Button
                  onClick={finalize}
                  disabled={submitting || !allRequiredUploaded}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="reg-finalize-btn"
                >
                  {submitting && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                  Submit for review
                </Button>
              ) : (
                <Button onClick={next} disabled={submitting} className="bg-blue-600 hover:bg-blue-700" data-testid="reg-next-btn">
                  {submitting && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                  {step === 3 ? (isPending ? 'Create account & continue' : 'Create account') : 'Next'}
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RegisterPage;
